import { useEffect, useRef, useState } from 'react';
import {
  Save, DollarSign, Calendar, Users, ShieldCheck, CheckCircle2, CircleDashed,
  Building2, Image as ImageIcon, Trash, AlertTriangle, ExternalLink,
} from 'lucide-react';
import supabase from '../lib/supabase';
import PhotoUploader from '../components/PhotoUploader';
import {
  PageHeader, Section, StatCard, Button, Badge, Field, Input, Textarea, Checkbox, StatusLine,
} from '../components/ui';

type Club = {
  id: string;
  name: string;
  address: string | null;
  description: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  accepts_freelance_coaches: boolean;
  cancel_full_cutoff_hours: number;
  cancel_half_cutoff_hours: number;
  cancel_processing_fee: number;
  is_active: boolean;
  stripe_onboarding_completed_at: string | null;
  photos: string[] | null;
  logo_url: string | null;
};

type Stats = {
  bookings30d: number;
  revenue30d: number;
  coaches: number;
};

/**
 * Pulls every form field out of a club row in one place so the load
 * path and the post-save sync don't drift.
 */
function applyClub(c: Club, set: {
  setName: (s: string) => void;
  setAddress: (s: string) => void;
  setDescription: (s: string) => void;
  setEmail: (s: string) => void;
  setPhone: (s: string) => void;
  setAcceptFreelance: (b: boolean) => void;
  setCancelFullHrs: (n: number) => void;
  setCancelHalfHrs: (n: number) => void;
  setCancelFee: (n: number) => void;
  setPhotos: (p: string[]) => void;
}) {
  set.setName(c.name);
  set.setAddress(c.address ?? '');
  set.setDescription(c.description ?? '');
  set.setEmail(c.contact_email ?? '');
  set.setPhone(c.contact_phone ?? '');
  set.setAcceptFreelance(c.accepts_freelance_coaches);
  set.setCancelFullHrs(c.cancel_full_cutoff_hours ?? 24);
  set.setCancelHalfHrs(c.cancel_half_cutoff_hours ?? 12);
  set.setCancelFee(Number(c.cancel_processing_fee ?? 20));
  set.setPhotos(Array.isArray(c.photos) ? c.photos : []);
}

export default function ClubOverview({ clubId }: { clubId: string }) {
  const [club, setClub] = useState<Club | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [acceptFreelance, setAcceptFreelance] = useState(true);
  const [cancelFullHrs, setCancelFullHrs] = useState(24);
  const [cancelHalfHrs, setCancelHalfHrs] = useState(12);
  const [cancelFee, setCancelFee] = useState(20);
  const [photos, setPhotos] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [stripeLinking, setStripeLinking] = useState(false);

  // Resume Stripe onboarding from the partner app. Admin (or our
  // ops team) generates the initial link during club setup; if the
  // owner doesn't finish in that window the link expires, and they
  // need a fresh one. This button mints a new link from the same
  // edge function and opens it inline so the owner doesn't have to
  // chase down the original email.
  const finishStripeSetup = async () => {
    if (!club) return;
    setStripeLinking(true);
    setMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        'stripe-connect-onboarding',
        { body: { clubId } },
      );
      if (error) {
        setMsg({ kind: 'error', text: (error as any).message ?? 'Could not generate Stripe link' });
        return;
      }
      if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
    } finally {
      setStripeLinking(false);
    }
  };

  const setters = {
    setName, setAddress, setDescription, setEmail, setPhone,
    setAcceptFreelance, setCancelFullHrs, setCancelHalfHrs, setCancelFee, setPhotos,
  };

  // load() now uses the new RPC for stats so we don't drag every
  // booking row across the wire to compute three numbers in JS.
  // Wrapped in try/finally so a failed fetch can't leave the page
  // permanently stuck on "Loading…" or "Saving…".
  const load = async ({ showSpinner = true }: { showSpinner?: boolean } = {}) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [clubRes, statsRes] = await Promise.all([
        supabase.from('clubs').select('*').eq('id', clubId).single(),
        (supabase.rpc as any)('club_dashboard_stats', { p_club_id: clubId }),
      ]);
      if (clubRes.error) throw clubRes.error;
      const c = clubRes.data as Club;
      setClub(c);
      applyClub(c, setters);

      // RPC returns a single row; supabase-js wraps table-returning
      // functions as an array. Tolerate both shapes for safety.
      const row = Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data;
      setStats({
        bookings30d: Number(row?.bookings_30d ?? 0),
        revenue30d: Number(row?.revenue_30d ?? 0),
        coaches: Number(row?.coaches ?? 0),
      });
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load club data.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [clubId]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      // .select('*').single() so the UI snaps to the freshly-saved
      // row rather than waiting for a separate reload — also surfaces
      // RLS denials (no row returned) as an explicit error rather
      // than pretending the save succeeded.
      const { data, error } = await supabase
        .from('clubs')
        .update({
          name,
          address: address || null,
          description: description || null,
          contact_email: email || null,
          contact_phone: phone || null,
          accepts_freelance_coaches: acceptFreelance,
          cancel_full_cutoff_hours: cancelFullHrs,
          cancel_half_cutoff_hours: cancelHalfHrs,
          cancel_processing_fee:    cancelFee,
          photos,
        })
        .eq('id', clubId)
        .select('*')
        .single();
      if (error) {
        // RLS "no rows" surfaces as PGRST116 from .single().
        if ((error as any).code === 'PGRST116') {
          setMsg({
            kind: 'error',
            text: "Nothing was saved — you don't have permission to edit this club. Ask an admin to add you as an owner or manager.",
          });
        } else {
          setMsg({ kind: 'error', text: error.message });
        }
        return;
      }
      const c = data as Club;
      setClub(c);
      // Re-sync every form field from the returned row so the dirty
      // state collapses and the cancel-policy / contact / freelance
      // toggles all reflect what's actually persisted.
      applyClub(c, setters);
      setMsg({ kind: 'success', text: 'Saved.' });
    } finally {
      setBusy(false);
    }
  };

  // Photo edits persist immediately so the user doesn't lose an upload
  // by navigating away before clicking "Save changes" on the settings
  // form. We also re-set the local club row from the returned data so
  // any subsequent save() includes the latest photos array.
  const onPhotosChange = async (next: string[]) => {
    setPhotos(next);
    const { data, error } = await supabase
      .from('clubs')
      .update({ photos: next })
      .eq('id', clubId)
      .select('*')
      .single();
    if (error) {
      if ((error as any).code === 'PGRST116') {
        setMsg({
          kind: 'error',
          text: "Photo update didn't persist — you don't have permission to edit this club.",
        });
      } else {
        setMsg({ kind: 'error', text: `Photos: ${error.message}` });
      }
      return;
    }
    setClub(data as Club);
  };

  const onLogoChange = async (url: string | null) => {
    // Optimistic — show the new logo right away while the write
    // round-trips. If it fails we fall back to the previous value.
    const previous = club?.logo_url ?? null;
    if (club) setClub({ ...club, logo_url: url });
    const { data, error } = await supabase
      .from('clubs')
      .update({ logo_url: url })
      .eq('id', clubId)
      .select('*')
      .single();
    if (error) {
      if (club) setClub({ ...club, logo_url: previous });
      setMsg({ kind: 'error', text: `Logo: ${error.message}` });
      return;
    }
    setClub(data as Club);
  };

  if (loading && !club) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (!club) {
    return (
      <div className="p-8">
        <StatusLine kind="error">
          {error ?? 'Club not found.'} <button className="underline ml-2" onClick={() => load()}>Retry</button>
        </StatusLine>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <PageHeader
        eyebrow="Club"
        title={club.name}
        subtitle={(
          <span className="flex items-center gap-2 flex-wrap">
            {club.is_active
              ? <Badge tone="green" icon={CheckCircle2}>Live on AceRent</Badge>
              : <Badge tone="gray" icon={CircleDashed}>Not live yet</Badge>}
            {club.stripe_onboarding_completed_at
              ? <Badge tone="lime" icon={CheckCircle2}>Stripe connected</Badge>
              : <Badge tone="amber">Stripe pending</Badge>}
          </span>
        )}
      />

      {error && (
        <div className="mb-4">
          <StatusLine kind="error">
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
              <button className="underline ml-1" onClick={() => load()}>Retry</button>
            </span>
          </StatusLine>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Calendar}    label="Bookings (30d)" value={stats?.bookings30d} />
        <StatCard icon={DollarSign}  label="Revenue (30d)"  value={stats ? `AED ${stats.revenue30d.toFixed(0)}` : undefined} />
        <StatCard icon={Users}       label="Staff coaches"  value={stats?.coaches} />
      </div>

      {/* Stripe-onboarding CTA — surfaced prominently because nothing
          else on the partner app blocks the way to "go live". The
          AceRent team mints the initial link during club setup and
          emails it; this is the resume button when the owner needs a
          fresh one. */}
      {!club.stripe_onboarding_completed_at && (
        <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-amber-900">
              Finish Stripe setup to start accepting bookings
            </div>
            <p className="text-xs text-amber-800 mt-1">
              Stripe Connect collects the bank details we'll pay your bookings into.
              The form takes about 5 minutes; once it's done, your club goes live in
              customer search automatically.
            </p>
          </div>
          <Button
            type="button"
            icon={ExternalLink}
            loading={stripeLinking}
            onClick={finishStripeSetup}
          >
            Finish Stripe setup
          </Button>
        </div>
      )}

      <Section
        title="Brand"
        subtitle="Your logo appears next to your club name on the customer app."
      >
        <LogoUploader
          clubId={clubId}
          url={club.logo_url}
          onChange={onLogoChange}
        />
      </Section>

      <Section
        title="Facility photos"
        subtitle="Shown to customers on your club page. The first photo is the cover image."
      >
        <PhotoUploader
          bucket="club-photos"
          pathPrefix={clubId}
          photos={photos}
          onChange={onPhotosChange}
          max={8}
        />
      </Section>

      <form onSubmit={save}>
        <Section title="Club settings" subtitle="Basic information shown on your public club page.">
          <div className="space-y-4">
            <Field label="Club name">
              <Input value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>

            <Field label="Short description" hint="Max 400 characters.">
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={400} />
            </Field>

            <Field label="Address">
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="e.g. Al Quoz, Dubai" />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Contact email">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ops@yourclub.ae" />
              </Field>
              <Field label="Contact phone">
                <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+971 …" />
              </Field>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-xl bg-[#c6f806]/10 border border-[#2d5016]/20">
              <ShieldCheck className="w-4 h-4 text-[#2d5016] shrink-0 mt-0.5" />
              <Checkbox
                checked={acceptFreelance}
                onChange={setAcceptFreelance}
                label="Accept verified freelance coaches"
                hint="When on, any coach verified by AceRent can offer lessons at your courts. You can still block specific coaches from the roster page."
              />
            </div>

            <div className="pt-2 border-t border-gray-100">
              <div className="text-sm font-medium text-gray-900 mb-1">Cancellation policy</div>
              <p className="text-xs text-gray-500 mb-3">
                Customers see this on their booking and when cancelling. Shorter cut-offs
                mean stricter refunds — be transparent.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="Full refund cut-off (hours)" hint="Cancel this many hours before start for a full refund.">
                  <Input type="number" min="0" step="1" value={String(cancelFullHrs)}
                    onChange={(e) => setCancelFullHrs(Number(e.target.value) || 0)} />
                </Field>
                <Field label="Half refund cut-off (hours)" hint="Below this, no refund.">
                  <Input type="number" min="0" step="1" value={String(cancelHalfHrs)}
                    onChange={(e) => setCancelHalfHrs(Number(e.target.value) || 0)} />
                </Field>
                <Field label="Processing fee (AED)" hint="Deducted from every full-refund cancellation.">
                  <Input type="number" min="0" step="1" value={String(cancelFee)}
                    onChange={(e) => setCancelFee(Number(e.target.value) || 0)} />
                </Field>
              </div>
            </div>

            {msg && <StatusLine kind={msg.kind}>{msg.text}</StatusLine>}

            <div>
              <Button type="submit" loading={busy} icon={Save}>
                Save changes
              </Button>
            </div>
          </div>
        </Section>
      </form>
    </div>
  );
}

/**
 * Single-image logo uploader stored alongside facility photos in the
 * club-photos bucket under `<club_id>/logo-...` so the same RLS applies.
 */
function LogoUploader({
  clubId, url, onChange,
}: {
  clubId: string;
  url: string | null;
  onChange: (url: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const stamp = Date.now().toString(36);
      const path = `${clubId}/logo-${stamp}.${ext}`;
      const { error } = await supabase.storage
        .from('club-photos')
        .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });
      if (error) { setErr(error.message); return; }
      const { data } = supabase.storage.from('club-photos').getPublicUrl(path);
      onChange(data.publicUrl);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    const marker = '/storage/v1/object/public/club-photos/';
    const idx = url?.indexOf(marker);
    if (url && idx !== undefined && idx >= 0) {
      const objectPath = url.slice(idx + marker.length);
      await supabase.storage.from('club-photos').remove([objectPath]);
    }
    onChange(null);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="w-24 h-24 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center overflow-hidden shrink-0">
        {url
          ? <img src={url} alt="" className="w-full h-full object-cover" />
          : <Building2 className="w-7 h-7 text-gray-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" icon={ImageIcon} loading={uploading}
            onClick={() => inputRef.current?.click()}>
            {url ? 'Replace logo' : 'Upload logo'}
          </Button>
          {url && (
            <Button size="sm" variant="danger" icon={Trash} onClick={remove}>
              Remove
            </Button>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          PNG / JPEG / WebP up to 10&nbsp;MB. Square images render best.
        </div>
        {err && <div className="text-xs text-red-700 mt-1">{err}</div>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/avif,image/svg+xml"
          className="hidden"
          onChange={(e) => onFile(e.target.files)}
        />
      </div>
    </div>
  );
}
